/**
 * Capability resolver — ported VERBATIM from the FGR React app
 * (`frontend/app/templates/capabilities/resolver.ts`). The only OT-specific
 * changes are: (1) `CapabilityUser` is the local clean shape from
 * `capability-user.adapter` instead of `PersonalBaseInfo`, and (2)
 * `hasStaffProfile` is inlined (FGR imports it from `viewerType.ts`).
 *
 * Keep this framework-agnostic and pure — it is unit-tested without TestBed.
 * The three load-bearing invariants below MUST NOT be "simplified" away.
 */
import { CapabilityUser } from './capability-user.adapter';
import {
  CAPABILITY_RULES,
  CapabilityName,
  CapabilityRule,
  ReqRole,
  ReqType,
} from './registry-ot.generated';

export type CapabilitiesMap = Record<CapabilityName, boolean>;

/**
 * INVARIANT 1: `req_type=staff` = "has a staff profile" = `types` includes
 * `'staff'` OR `'agent'`. `staff_type` is `'agent'` XOR `'staff'`, so a staff
 * agent is `types:['agent']`; a naive `types.includes('staff')` would deny every
 * staff capability to agents.
 */
const hasStaffProfile = (user: CapabilityUser | null): boolean =>
  !!user && (user.types.includes('staff') || user.types.includes('agent'));

/**
 * INVARIANT 3: `req_type=client` = the client VIEWER — a client profile
 * WITHOUT a staff profile. The backend appends `'client'` to `types` for any
 * user with a client_profile, so a dual-profile agent is
 * `types:['agent','client']`; a naive `types.includes('client')` would grant
 * staff every client-eligible capability (FGR-5774 QA: agents saw the public
 * Share / Request Tour / Get Pre-Qualified CTAs). No OT row carries
 * `req_type=client` today — this guards future `both`-surface rows.
 */
const isClientViewer = (user: CapabilityUser | null): boolean =>
  !!user && user.types.includes('client') && !hasStaffProfile(user);

const matchType = (reqType: readonly ReqType[], user: CapabilityUser | null): boolean => {
  if (reqType.length === 0) {
    return true; // no type constraint
  }
  return reqType.some((type) => {
    switch (type) {
      case 'any':
        return true;
      case 'anon':
        return !user;
      case 'staff':
        return hasStaffProfile(user);
      case 'client':
        return isClientViewer(user);
      default:
        return false;
    }
  });
};

/**
 * INVARIANT 2: `req_role` reads the UserRole flags (`roles.is_agent` /
 * `is_teamlead` / `is_company_admin`). `roles.is_agent` is ORTHOGONAL to `types`'
 * `'agent'` (the staff_type): the role flag is the access mechanism, the
 * staff_type is not. A `staff_type='agent'` user WITHOUT `is_agent` does not get
 * agent capabilities.
 */
const matchRole = (reqRole: readonly ReqRole[], user: CapabilityUser | null): boolean => {
  if (reqRole.length === 0) {
    return true; // no role constraint
  }
  if (!user) {
    return false;
  }
  return reqRole.some((role) => Boolean(user.roles[`is_${role}`]));
};

const matchCode = (reqCode: CapabilityRule['reqCode'], user: CapabilityUser | null): boolean => {
  if (!reqCode) {
    return true; // no code constraint
  }
  if (!user) {
    return false;
  }
  return user.permissions.includes(reqCode);
};

export const resolveRule = (rule: CapabilityRule, user: CapabilityUser | null): boolean =>
  matchType(rule.reqType, user) &&
  matchRole(rule.reqRole, user) &&
  matchCode(rule.reqCode, user);

export const resolveCapabilities = (user: CapabilityUser | null): CapabilitiesMap => {
  const map = {} as CapabilitiesMap;
  CAPABILITY_RULES.forEach((rule) => {
    map[rule.name] = resolveRule(rule, user);
  });
  return map;
};

const RULES_BY_NAME = new Map<CapabilityName, CapabilityRule>(
  CAPABILITY_RULES.map((rule): [CapabilityName, CapabilityRule] => [rule.name, rule])
);

/** Single-capability check — for guards, services, and TS logic (deal-matrix). */
export const checkCapability = (name: CapabilityName, user: CapabilityUser | null): boolean => {
  const rule = RULES_BY_NAME.get(name);
  return rule ? resolveRule(rule, user) : false;
};
